/**
 * Store App Opener Utility
 * Ensures that if a user has a branded store mobile app installed,
 * links/actions open directly inside the app. If the app is not installed,
 * it seamlessly falls back to opening the store's web portal in the browser.
 */

export interface StoreAppOpenOptions {
  store: string;
  webUrl?: string;
  action?: 'checkout' | 'cart' | 'product' | 'search' | 'general';
  searchQuery?: string;
  orderId?: string;
  forceWeb?: boolean;
}

interface StoreAppConfig {
  scheme: string;
  androidPackage: string;
  defaultWebUrl: string;
  buildAppUrl: (action: string, searchQuery?: string, orderId?: string, webUrl?: string) => { 
    deepLink: string; 
    intentPath: string; 
    intentScheme?: string;
    resolvedWebUrl: string;
  };
}

const STORE_CONFIGS: Record<string, StoreAppConfig> = {
  silpo: {
    scheme: 'silpo',
    androidPackage: 'ua.silpo.android',
    defaultWebUrl: 'https://silpo.ua/checkout',
    buildAppUrl: (action, searchQuery, _orderId, customWebUrl) => {
      if (searchQuery) {
        const q = encodeURIComponent(searchQuery);
        return { 
          deepLink: `silpo://search?find=${q}`, 
          intentPath: `search?find=${q}`, 
          intentScheme: 'silpo',
          resolvedWebUrl: `https://silpo.ua/search?find=${q}` 
        };
      }
      if (customWebUrl && customWebUrl.includes('/product/')) {
        const cleanHost = customWebUrl.replace(/^https?:\/\//, '');
        const cleanPath = customWebUrl.replace(/^https?:\/\/[^/]+\//, '');
        return {
          deepLink: `silpo://${cleanPath || cleanHost}`,
          intentPath: cleanPath || cleanHost,
          intentScheme: 'silpo',
          resolvedWebUrl: customWebUrl
        };
      }
      if (action === 'checkout') {
        return { 
          deepLink: 'silpo://checkout', 
          intentPath: 'checkout', 
          intentScheme: 'silpo',
          resolvedWebUrl: customWebUrl || 'https://silpo.ua/checkout' 
        };
      }
      if (action === 'cart') {
        return { 
          deepLink: 'silpo://basket', 
          intentPath: 'basket', 
          intentScheme: 'silpo',
          resolvedWebUrl: customWebUrl || 'https://silpo.ua/basket' 
        };
      }
      return { 
        deepLink: 'silpo://', 
        intentPath: '', 
        intentScheme: 'silpo',
        resolvedWebUrl: customWebUrl || 'https://silpo.ua' 
      };
    }
  },
  atb: {
    scheme: 'atbmarket',
    androidPackage: 'com.atb.atbmarket',
    defaultWebUrl: 'https://www.atbmarket.com/cart',
    buildAppUrl: (action, searchQuery, _orderId, customWebUrl) => {
      if (searchQuery) {
        const q = encodeURIComponent(searchQuery);
        return { 
          deepLink: `atbmarket://search?q=${q}`, 
          intentPath: `search?q=${q}`, 
          intentScheme: 'atbmarket',
          resolvedWebUrl: `https://www.atbmarket.com/search?q=${q}` 
        };
      }
      if (action === 'checkout' || action === 'cart') {
        return { 
          deepLink: 'atbmarket://cart', 
          intentPath: 'cart', 
          intentScheme: 'atbmarket',
          resolvedWebUrl: 'https://www.atbmarket.com/cart' 
        };
      }
      return { 
        deepLink: 'atbmarket://', 
        intentPath: '', 
        intentScheme: 'atbmarket',
        resolvedWebUrl: customWebUrl || 'https://www.atbmarket.com' 
      };
    }
  },
  fozzy: {
    scheme: 'fozzy',
    androidPackage: 'ua.zakaz.fozzy',
    defaultWebUrl: 'https://fozzy.zakaz.ua/uk/cart/',
    buildAppUrl: () => ({ 
      deepLink: 'fozzy://', 
      intentPath: '', 
      intentScheme: 'fozzy',
      resolvedWebUrl: 'https://fozzy.zakaz.ua/uk/cart/' 
    })
  },
  instacart: {
    scheme: 'instacart',
    androidPackage: 'com.instacart.client',
    defaultWebUrl: 'https://www.instacart.com/store/checkout',
    buildAppUrl: (action, searchQuery) => {
      if (searchQuery) {
        const q = encodeURIComponent(searchQuery);
        return { 
          deepLink: `instacart://search?query=${q}`, 
          intentPath: `store/search?query=${q}`, 
          intentScheme: 'instacart',
          resolvedWebUrl: `https://www.instacart.com/store/search?query=${q}` 
        };
      }
      return { 
        deepLink: 'instacart://cart', 
        intentPath: 'cart', 
        intentScheme: 'instacart',
        resolvedWebUrl: 'https://www.instacart.com/store/checkout' 
      };
    }
  },
  walmart: {
    scheme: 'walmart',
    androidPackage: 'com.walmart.android',
    defaultWebUrl: 'https://www.walmart.com/cart',
    buildAppUrl: (action, searchQuery) => {
      if (searchQuery) {
        const q = encodeURIComponent(searchQuery);
        return { 
          deepLink: `walmart://search?query=${q}`, 
          intentPath: `search?query=${q}`, 
          intentScheme: 'walmart',
          resolvedWebUrl: `https://www.walmart.com/search?query=${q}` 
        };
      }
      return { 
        deepLink: 'walmart://cart', 
        intentPath: 'cart', 
        intentScheme: 'walmart',
        resolvedWebUrl: 'https://www.walmart.com/cart' 
      };
    }
  },
  amazon_fresh: {
    scheme: 'com.amazon.mobile.shopping',
    androidPackage: 'com.amazon.mShop.android.shopping',
    defaultWebUrl: 'https://www.amazon.com/alm/storefront?almBrandId=VUZHIEZyZXNo',
    buildAppUrl: () => ({ 
      deepLink: 'com.amazon.mobile.shopping://', 
      intentPath: '', 
      intentScheme: 'com.amazon.mobile.shopping',
      resolvedWebUrl: 'https://www.amazon.com/alm/storefront?almBrandId=VUZHIEZyZXNo' 
    })
  }
};

export function openStoreBrandedAppOrWeb(options: StoreAppOpenOptions): void {
  const storeKey = (options.store || 'silpo').toLowerCase();
  const cfg = STORE_CONFIGS[storeKey] || STORE_CONFIGS.silpo;
  const action = options.action || 'general';

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isAndroid = /Android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || (typeof navigator !== 'undefined' && /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
  const isMobile = isAndroid || isIOS || (typeof window !== 'undefined' && window.innerWidth < 820);

  const { deepLink, intentPath, intentScheme, resolvedWebUrl } = cfg.buildAppUrl(action, options.searchQuery, options.orderId, options.webUrl);
  const webUrl = options.webUrl || resolvedWebUrl || cfg.defaultWebUrl;

  // Direct Web Navigation override (e.g. when app is not connected or explicitly using browser checkout)
  if (options.forceWeb) {
    if (isMobile) {
      window.location.assign(webUrl);
    } else {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  // 1. ANDROID: Use official Android App Link Intent format with fallback
  // This explicitly binds the domain to the verified Android app package, opening the native app directly.
  if (isAndroid) {
    const cleanPath = intentPath.replace(/^https?:\/\//, '').replace(/^\/+/, '');
    const scheme = intentScheme || cfg.scheme || 'silpo';
    const intentUri = `intent://${cleanPath}#Intent;scheme=${scheme};package=${cfg.androidPackage};S.browser_fallback_url=${encodeURIComponent(webUrl)};end`;

    // Attempt direct intent navigation
    try {
      window.location.href = intentUri;
    } catch {
      window.location.href = deepLink;
    }
    return;
  }

  // 2. iOS: Attempt deep link with pagehide/blur/visibility change detection
  if (isIOS) {
    let appOpened = false;
    const onAppExit = () => {
      appOpened = true;
    };

    window.addEventListener('pagehide', onAppExit, { once: true });
    window.addEventListener('blur', onAppExit, { once: true });
    const handleVis = () => {
      if (document.hidden) appOpened = true;
    };
    document.addEventListener('visibilitychange', handleVis, { once: true });

    // Open deep link directly
    window.location.href = deepLink;

    // Generous fallback timeout (4000ms) to ensure iOS "Open in App" prompt is not interrupted
    setTimeout(() => {
      window.removeEventListener('pagehide', onAppExit);
      window.removeEventListener('blur', onAppExit);
      document.removeEventListener('visibilitychange', handleVis);
      if (!appOpened && !document.hidden) {
        // App was not installed, navigate to the web URL directly
        window.location.assign(webUrl);
      }
    }, 4000);
    return;
  }

  // 3. Desktop / Mobile Web fallback:
  if (isMobile) {
    let appOpened = false;
    const handleVis = () => {
      if (document.hidden) appOpened = true;
    };
    document.addEventListener('visibilitychange', handleVis, { once: true });
    window.location.href = deepLink;
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVis);
      if (!appOpened && !document.hidden) {
        window.location.assign(webUrl);
      }
    }, 3000);
  } else {
    // Desktop browser: open the official store web portal in a clean new tab
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  }
}
